import { AbstractControl, UntypedFormArray, UntypedFormControl, ValidationErrors } from '@angular/forms';
import { CheckoutUpsellExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';

import DiscountMode = CheckoutUpsellExtension.DiscountMode;
export { DiscountMode };

export interface AutonomousDiscountConfiguration extends CheckoutUpsellExtension.ExtensionDiscountConfiguration {
  zeroDiscountLabelText?: string;
}

export interface DiscountSectionTemplate {
  applyDiscountToRecommendedProducts?: boolean;
  discountConfiguration?: AutonomousDiscountConfiguration;
  productSelection?: object[];
}

export const DEFAULT_ALLOWED_DISCOUNT_VALUES: readonly number[] = [0, 5, 10, 15];

export const MIN_ALLOWED_DISCOUNT_VALUES = 2;
export const MAX_ALLOWED_DISCOUNT_VALUES = 10;

export function isAutonomousGroup(control: AbstractControl | null | undefined): boolean {
  return control?.get('mode')?.value === DiscountMode.Autonomous;
}

export function allowedDiscountValueValidator(control: AbstractControl): ValidationErrors | null {
  const discountConfiguration = control.parent?.parent;
  if (!isAutonomousGroup(discountConfiguration)) {
    return null;
  }

  const value = control.value;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 100) {
    return { allowedDiscountValue: '0 to 100' };
  }
  return null;
}

export function createAllowedDiscountValueControl(value: number): UntypedFormControl {
  return new UntypedFormControl(value, allowedDiscountValueValidator);
}

export function refreshAllowedDiscountValueValidity(allowedDiscountValues: UntypedFormArray): void {
  allowedDiscountValues.controls.forEach((row) => row.updateValueAndValidity({ onlySelf: true, emitEvent: false }));
  allowedDiscountValues.updateValueAndValidity();
}

export function autonomousDiscountValidator(group: AbstractControl): ValidationErrors | null {
  if (!isAutonomousGroup(group)) {
    return null;
  }

  const values: number[] = group.get('allowedDiscountValues')?.value ?? [];

  if (values.length < MIN_ALLOWED_DISCOUNT_VALUES || values.length > MAX_ALLOWED_DISCOUNT_VALUES) {
    return {
      allowedDiscountValues: `Add between ${MIN_ALLOWED_DISCOUNT_VALUES} and ${MAX_ALLOWED_DISCOUNT_VALUES} values`,
    };
  }
  if (!values.some((value) => value > 0)) {
    return { allowedDiscountValues: 'At least one value must be above 0' };
  }
  if (new Set(values).size !== values.length) {
    return { allowedDiscountValues: 'Values must be different' };
  }
  return null;
}

export function resolveAllowedDiscountValuesOnEnable(current: number[]): number[] {
  return current.length > 0 ? current : [...DEFAULT_ALLOWED_DISCOUNT_VALUES];
}

export function resolveShowLegacyDiscountControls(template: DiscountSectionTemplate | null | undefined): boolean {
  if (!template) {
    return false;
  }
  if (template.discountConfiguration?.mode === DiscountMode.Autonomous) {
    return false;
  }
  if (template.applyDiscountToRecommendedProducts === true) {
    return true;
  }
  const savedByPerProductDiscountUi = (template.productSelection ?? []).some(
    (ruleGroup: object) => 'discounts' in ruleGroup,
  );
  return !!template.discountConfiguration && !savedByPerProductDiscountUi;
}

export function resolveShowDiscountSection(
  showLegacyDiscountControls: boolean,
  isAutonomous: boolean,
  flagOn: boolean,
): boolean {
  return showLegacyDiscountControls || isAutonomous || flagOn;
}

export function resolveShowStaticDiscountControls(isAutonomous: boolean, flagOn: boolean): boolean {
  return flagOn || !isAutonomous;
}

export interface GroupDiscountState {
  applyDiscountToRecommendedProducts?: boolean;
  discounts?: unknown[];
}

export function hasActiveGroupDiscounts(ruleGroups: GroupDiscountState[]): boolean {
  return ruleGroups.some((ruleGroup) => ruleGroup?.applyDiscountToRecommendedProducts === true);
}

export function resolveAutonomousFallbackValue(
  discountType: CheckoutUpsellExtension.DiscountType | undefined,
  discountValue: number | undefined,
): number {
  return discountType === CheckoutUpsellExtension.DiscountType.Percentage ? (discountValue ?? 0) : 0;
}

export interface StaticDiscountSnapshot {
  discountType: CheckoutUpsellExtension.DiscountType | undefined;
  discountValue: number | undefined;
}

export const AUTONOMOUS_LABEL_VALUE_PLACEHOLDER = '{value}';

export function resolveAutonomousDiscountLabel(
  configuration: AutonomousDiscountConfiguration | null | undefined,
  chosenValue: number | undefined,
): string {
  if (!chosenValue) {
    return configuration?.zeroDiscountLabelText ?? '';
  }
  return (configuration?.labelText ?? '').split(AUTONOMOUS_LABEL_VALUE_PLACEHOLDER).join(String(chosenValue));
}

export function resolveShowAutonomousMark(flagOn: boolean, mode: DiscountMode | undefined): boolean {
  return flagOn && mode === DiscountMode.Autonomous;
}

export function prepareDiscountConfigurationForSave(
  value: AutonomousDiscountConfiguration,
  flagOn: boolean,
): AutonomousDiscountConfiguration {
  const prepared: AutonomousDiscountConfiguration = { ...value };

  if (!flagOn) {
    delete prepared.mode;
    delete prepared.allowedDiscountValues;
    return prepared;
  }

  prepared.mode = prepared.mode ?? DiscountMode.Static;
  prepared.allowedDiscountValues = [...(prepared.allowedDiscountValues ?? [])].sort((a, b) => a - b);

  if (prepared.mode === DiscountMode.Autonomous) {
    prepared.discountType = CheckoutUpsellExtension.DiscountType.Percentage;
  }

  return prepared;
}
